import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./styles.module.scss";
import MoreIcon from "../../../../public/images/more.svg";
import EditIcon from "../../../../public/images/edit-black.svg";
import TurnOnOffIcon from "../../../../public/images/turn-on-off.svg";
import DeleteIcon from "../../../../public/images/delete-red.svg";
import ResourceIcon from "app/components/ResourceIcon";
import { cls } from "app/utils";
import useCloseOnOutsideClick from "./useCloseOnOutsideClick";
import { ScheduleResourceResponse } from "app/utils/request/scheduling/facilityResources";

interface CardMenuProps {
  resource: ScheduleResourceResponse;
  onEdit: (resource: ScheduleResourceResponse) => void;
  onToggleActive: (resource: ScheduleResourceResponse) => void;
  onDelete: (resourceId: number) => void;
}

// The reorder mode gives a drag handle and no menu actions.
interface CardDragProps {
  resource: ScheduleResourceResponse;
  dragHandle: React.ReactNode;
}

// The read-only page gives no handle and no menu.
interface CardReadOnlyProps {
  resource: ScheduleResourceResponse;
}

type ResourceCardProps = CardMenuProps | CardDragProps | CardReadOnlyProps;

/**
 * The "..." menu of a card (Figma 68:7148). The menu order is Edit,
 * Deactivate or Activate, Delete.
 */
const CardMenu: React.FC<CardMenuProps> = ({
  resource,
  onEdit,
  onToggleActive,
  onDelete,
}) => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  // Holds the trigger and the menu, so a click on the trigger is not a click
  // outside.
  const menuRef = useRef<HTMLDivElement>(null);

  useCloseOnOutsideClick(menuRef, menuOpen, () => setMenuOpen(false));

  const choose = (action: () => void) => {
    setMenuOpen(false);
    action();
  };

  return (
    <div className={styles.menu_anchor} ref={menuRef}>
      <button
        type="button"
        className={styles.more_button}
        onClick={() => setMenuOpen(!menuOpen)}
        aria-label={t("Resource actions")}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
      >
        <img src={MoreIcon} alt="" width={24} height={24} />
      </button>
      {menuOpen && (
        <div
          className={cls(styles.action_menu, styles.action_menu_card)}
          role="menu"
          data-testid="resource-card-menu"
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => choose(() => onEdit(resource))}
            className={styles.menu_item}
          >
            <img src={EditIcon} alt="" width={24} height={24} />
            {t("Edit")}
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => choose(() => onToggleActive(resource))}
            className={styles.menu_item}
          >
            <img src={TurnOnOffIcon} alt="" width={24} height={24} />
            {resource.active ? t("Deactivate") : t("Activate")}
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => choose(() => onDelete(resource.id))}
            className={cls(styles.menu_item, styles.menu_item_danger)}
          >
            <img src={DeleteIcon} alt="" width={24} height={24} />
            {t("Delete")}
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * One resource on the Resources page (Figma 68:7019): the icon tile, the
 * title with the status chip under it, and a "..." menu. In the reorder mode
 * (Figma 83:26633) the card has a drag handle before the tile and no menu.
 * On the read-only page the card has no handle and no menu.
 */
const ResourceCard: React.FC<ResourceCardProps> = (props) => {
  const { resource } = props;
  const { t } = useTranslation();

  return (
    <div className={styles.row_card} data-testid="resource-card">
      {"dragHandle" in props && props.dragHandle}
      <div
        className={cls(styles.row_icon_tile, {
          [styles.row_icon_tile_active]: resource.active,
        })}
      >
        <ResourceIcon size={24} />
      </div>
      <div className={styles.row_text}>
        <div className={styles.row_title}>{resource.title}</div>
        <span
          className={resource.active ? styles.pill_active : styles.pill_inactive}
        >
          {resource.active ? t("Active") : t("Inactive")}
        </span>
      </div>
      {"onEdit" in props && <CardMenu {...props} />}
    </div>
  );
};

export default ResourceCard;
